import React, { useState } from "react";
import { Sparkles, Instagram, Linkedin, Facebook, ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [facility, setFacility] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast.error("Please fill in your name and email");
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/quote`, { name, email, phone, facility });
      if (res.data?.success) {
        toast.success("Thanks! A Tidy specialist will reach out within 24 hours.");
        setName("");
        setEmail("");
        setPhone("");
        setFacility("");
      } else {
        toast.error("Something went wrong. Please try again or email us directly.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit right now. Please email trytidycleanup@gmail.com instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#143527] text-[#f4ebde]">
      {/* Quote section */}
      <section id="quote" className="py-24 lg:py-32 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#e8a428] mb-4">
              Get a Quote
            </p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] mb-6">
              Tell us about your space.
            </h2>
            <p className="text-[16px] text-[#f4ebde]/75 leading-relaxed mb-10 max-w-md">
              Share a few details and a Tidy specialist will reach out within 24 hours with a tailored cleaning plan.
            </p>

            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-3 text-[#f4ebde]/85">
                <Phone className="w-4 h-4 text-[#e8a428]" />
                <span>(910) 603-9850</span>
              </div>
              <div className="flex items-center gap-3 text-[#f4ebde]/85">
                <Mail className="w-4 h-4 text-[#e8a428]" />
                <a href="mailto:trytidycleanup@gmail.com" className="hover:text-[#e8a428] transition-colors">
                  trytidycleanup@gmail.com
                </a>
              </div>
              <div className="flex items-center gap-3 text-[#f4ebde]/85">
                <MapPin className="w-4 h-4 text-[#e8a428]" />
                <span>Southern Pines, NC · Serving the Carolinas</span>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-[#f4ebde] rounded-3xl p-8 lg:p-10 text-[#143527]"
          >
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2">Full name *</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="bg-white border-[#143527]/20 h-12 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="bg-white border-[#143527]/20 h-12 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(910) 555-0100"
                  className="bg-white border-[#143527]/20 h-12 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tell us about your facility</label>
                <textarea
                  value={facility}
                  onChange={(e) => setFacility(e.target.value)}
                  rows={4}
                  placeholder="Aviation hangar, 12,000 sq ft office, post-event venue..."
                  className="w-full bg-white border border-[#143527]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#143527]/30"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-full w-full py-6 btn-primary text-[15px] group disabled:opacity-70"
              >
                {submitting ? "Sending..." : "Request my quote"}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Bottom footer */}
      <div className="border-t border-[#f4ebde]/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-6 h-6 text-[#e8a428]" strokeWidth={1.8} />
              <span className="font-serif text-3xl">tidy</span>
            </div>
            <p className="font-serif italic text-[#e8a428] text-base mb-5">
              We handle the dirty work.
            </p>
            <p className="text-sm text-[#f4ebde]/65 leading-relaxed max-w-sm mb-6">
              Your facility services partner for aviation, commercial, industrial and event facilities
              across Southern Pines, Moore County and the Carolinas.
            </p>
            <div className="flex gap-3">
              {[Instagram, Linkedin, Facebook].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-10 h-10 rounded-full border border-[#f4ebde]/25 flex items-center justify-center hover:bg-[#e8a428] hover:border-[#e8a428] hover:text-[#143527] transition-colors"
                  aria-label="Social link"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-[#e8a428] mb-4">Services</h4>
            <ul className="space-y-3 text-sm text-[#f4ebde]/75">
              <li><a href="#services" className="hover:text-[#e8a428]">Aviation Services</a></li>
              <li><a href="#services" className="hover:text-[#e8a428]">Commercial Janitorial</a></li>
              <li><a href="#services" className="hover:text-[#e8a428]">Industrial &amp; Warehouse</a></li>
              <li><a href="#services" className="hover:text-[#e8a428]">Events &amp; Specialty</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-[#e8a428] mb-4">Company</h4>
            <ul className="space-y-3 text-sm text-[#f4ebde]/75">
              <li><a href="#why-tidy" className="hover:text-[#e8a428]">Why Tidy</a></li>
              <li><a href="#how-it-works" className="hover:text-[#e8a428]">How It Works</a></li>
              <li><a href="#service-areas" className="hover:text-[#e8a428]">Service Areas</a></li>
              <li><a href="#faq" className="hover:text-[#e8a428]">FAQ</a></li>
              <li><a href="#quote" className="hover:text-[#e8a428]">Get a Quote</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[#f4ebde]/15">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#f4ebde]/55">
            <p>© {new Date().getFullYear()} Tidy Cleaning Co. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-[#e8a428]">Privacy</a>
              <a href="#" className="hover:text-[#e8a428]">Terms</a>
              <a href="#" className="hover:text-[#e8a428]">Careers</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
